/**
 * Helper functions for extracting and working with Zones from Ramp shapes
 */

import { Ramp } from "../../../types";
import { Shape, ZoneMetadata } from "../../../screens/Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import { Zone } from "./types";

/**
 * Calculate the center point of a spot's bounding box
 */
function getSpotCenter(spot: Shape): { x: number; y: number } {
  if (!spot.points || spot.points.length === 0) {
    return { x: 0, y: 0 };
  }

  const xs = spot.points.map(p => p.x);
  const ys = spot.points.map(p => p.y);

  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
}

/**
 * Check if a point is inside a polygon using ray casting algorithm
 * @param point - The point to test
 * @param polygon - Array of points defining the polygon
 * @returns true if point is inside polygon
 */
function isPointInPolygon(point: { x: number; y: number }, polygon: { x: number; y: number }[]): boolean {
  if (!polygon || polygon.length < 3) {
    return false;
  }

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Assign spots to zones based on spatial containment
 * A spot belongs to a zone if its center point is inside the zone's polygon
 *
 * @param spots - Array of Spot shapes
 * @param zoneShapes - Array of Zone shapes from the ramp
 * @returns Map of zoneId to array of spot IDs
 */
export function assignSpotsToZones(
  spots: Shape[],
  zoneShapes: Shape[]
): Map<string, string[]> {
  const zoneToSpots = new Map<string, string[]>();

  // Initialize map with empty arrays for each zone
  zoneShapes.forEach((zone) => {
    zoneToSpots.set(zone.id, []);
  });

  // For each spot, find which zone(s) contain it
  spots.forEach((spot) => {
    const center = getSpotCenter(spot);

    zoneShapes.forEach((zone) => {
      if (isPointInPolygon(center, zone.points)) {
        zoneToSpots.get(zone.id)?.push(spot.id);
      }
    });
  });

  return zoneToSpots;
}

/**
 * Extract zones from a ramp's shapes with spot assignments
 * Zones are shapes with type="Zone" and zone properties stored in metadata
 *
 * @param ramp - The ramp containing shapes
 * @returns Array of Zone objects for use in the temporal parking algorithm
 */
export function extractZonesFromRamp(ramp: Ramp): Zone[] {
  if (!ramp?.shapes) {
    return [];
  }

  const zoneShapes = ramp.shapes.filter((s) => s.type === "Zone");
  const spotShapes = ramp.shapes.filter((s) => s.type === "Spot");

  // Get spatial assignments
  const zoneToSpots = assignSpotsToZones(spotShapes, zoneShapes);

  return zoneShapes.map((shape) => ({
    id: shape.id,
    name: shape.text || `Zone ${shape.id.slice(0, 8)}`,
    spotIds: zoneToSpots.get(shape.id) || [],
    priority: shape.priority ?? 5, // Use shape.priority column (single source of truth)
    shapeId: shape.id,
  }));
}

/**
 * Find the zone that contains a given spot
 * This is a placeholder for future functionality when spots can be assigned to zones
 *
 * @param spotId - The ID of the spot to find
 * @param zones - Array of zones to search
 * @returns The zone containing the spot, or undefined if not found
 */
export function findZoneForSpot(spotId: string, zones: Zone[]): Zone | undefined {
  return zones.find((zone) => zone.spotIds.includes(spotId));
}

/**
 * Assign a spot to a zone
 * This is a placeholder for future functionality when spots can be assigned to zones
 *
 * @param spotId - The ID of the spot to assign
 * @param zoneId - The ID of the zone to assign the spot to
 * @param zones - Array of zones to update
 * @returns Updated array of zones with the spot assigned
 */
export function assignSpotToZone(
  spotId: string,
  zoneId: string,
  zones: Zone[]
): Zone[] {
  return zones.map((zone) => {
    if (zone.id === zoneId) {
      // Add spot to this zone if not already present
      if (!zone.spotIds.includes(spotId)) {
        return {
          ...zone,
          spotIds: [...zone.spotIds, spotId],
        };
      }
    } else {
      // Remove spot from other zones
      return {
        ...zone,
        spotIds: zone.spotIds.filter((id) => id !== spotId),
      };
    }
    return zone;
  });
}
